import { createListenerMiddleware } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import i18n from 'i18n';
import { addAppAlertDialog, addAppErrorMessage, addAppInfoMessage, addAppSuccessMessage } from 'modules/alerts/state';
import { AppRoutes } from 'modules/router';
import { push } from 'redux-first-history';
import { handleQueryError } from 'store/utils';

import {
  InsightsReportStatus,
  createInsightsReportsRejectedEffect,
  createInsightsReportsSucceededEffect,
} from '../insightsReport';
import { captureExecutiveSummaryVersion } from './captureExecutiveSummaryVersion';
import { eventsReportApi } from './eventsReportApi';

import type { RootState } from 'store';

// TODO extract effects in next iteration
export const eventsReportApiLister = createListenerMiddleware<RootState>();

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.getEventsReportKeyMetrics.matchRejected,
  effect: ({ payload, type }, { dispatch }) => {
    handleQueryError(payload, dispatch, type, "Unable to fetch Events Report's key metrics");
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.retryEventsReportGeneration.matchFulfilled,
  effect: (_result, { dispatch }) => {
    dispatch(
      addAppAlertDialog({
        title: i18n.t('insightReport:wizard.retryEventsReportGeneration.title'),
        description: i18n.t('insightReport:wizard.retryEventsReportGeneration.description'),
        // TODO "Remove CTA and add a close icon? Jacopo Tortora d'Amato" - so soon will be removed
        confirmLabel: 'Got it',
      })
    );
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.retryEventsReportGeneration.matchRejected,
  effect: ({ payload, type }, { dispatch }) => {
    handleQueryError(
      payload,
      dispatch,
      type,
      'Report creation unsuccessful. Please retry, or contact support@natter.co if the issue persists.'
    );
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.updateEventsReport.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { name } = meta.arg.originalArgs;
    dispatch(addAppSuccessMessage(`${name} successfully updated`));
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.updateEventsReport.matchRejected,
  effect: ({ payload, type, meta }, { dispatch }) => {
    const { name } = meta.arg.originalArgs;
    handleQueryError(payload, dispatch, type, `Unable to update Report "${name}"`);
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.deleteEventsReport.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { name, status } = meta.arg.originalArgs;

    if (status === InsightsReportStatus.Error) {
      dispatch(addAppInfoMessage(`Unable to delete '${name}' report`));
    } else {
      dispatch(
        addAppAlertDialog({
          title: 'Report Successfully Deleted',
          description: `Your Report ${name} was successfully deleted from the Reports List.`,
          confirmLabel: 'Ok',
        })
      );
    }

    dispatch(push(AppRoutes.InsightsReports));
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.deleteEventsReport.matchRejected,
  effect: ({ payload, type, meta }, { dispatch }) => {
    const { name } = meta.arg.originalArgs;
    handleQueryError(payload, dispatch, type, `Unable to delete Report "${name}"`);
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.createEventsReport.matchFulfilled,
  effect: createInsightsReportsSucceededEffect,
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.createEventsReport.matchRejected,
  effect: createInsightsReportsRejectedEffect,
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.exportEventsReport.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppInfoMessage('You will be notified via email once the export is complete'));
  },
});

eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.exportEventsReport.matchRejected,
  effect: (action, { dispatch }) => {
    logger.error(
      'Events report export failed',
      { context: 'eventsReportApi.exportEventsReport' },
      action.payload ?? action.error
    );
    dispatch(
      addAppErrorMessage(
        'There was a failure during the export process. Please try again, or contact support@natter.co if the issue persists.'
      )
    );
  },
});

// Capture executive summary version to Sentry for temporary version history
eventsReportApiLister.startListening({
  matcher: eventsReportApi.endpoints.updateExecutiveSummary.matchFulfilled,
  effect: ({ meta }) => {
    const { reportId, content, prevContent } = meta.arg.originalArgs;

    captureExecutiveSummaryVersion({
      reportId,
      prevContent,
      newContent: content,
    });
  },
});
